import type { Component } from '../../router';
import { HealthPill } from '../HealthPill';
import { icon } from '../icons';
import { isActiveTab, isAdminArea, topNavItems, tabBarItems, adminMenuItems } from './navModel';
import { AccountMenu } from './AccountMenu';
import { t } from '../../i18n';
import { rbacContext } from '../../services/capabilities';
import { promptCompanyPicker } from '../CompanyPicker';
import { switchCompany } from '../../services/auth';
import type { CompanyMembership } from '../../api/client';

// AppNav — the two navigation primitives, formalized out of AppShell so
// each is an independently mountable, testable layout component. Both read the
// shared navModel and filter by the RBAC mirror; which one is visible is
// pure CSS (nav.css flips at 768px), so the shell mounts both and lets the
// viewport choose.
//
// Both expose refresh(), which rebuilds only the filtered link set in place.
// Grants are per-company, so a company switch changes what belongs in the nav
// (RBAC-006.7); rebuilding in place — rather than remounting — keeps each nav's
// position in the shell and avoids tearing down the surrounding layout.

// buildCompanyElement renders the active-company element for the top nav's
// identity cluster (FE-022): an interactive switch trigger when the caller holds
// more than one company — exact parity with the account menu's canSwitchCompany
// gating (FE-022.3) — or a plain, non-interactive label otherwise. Exported so
// the visual catalog renders the same element the live nav does.
export function buildCompanyElement(
  name: string,
  canSwitch: boolean,
  onSwitch: () => void,
): HTMLElement {
  if (!canSwitch) {
    const label = document.createElement('span');
    label.className = 'topnav__company';
    const text = document.createElement('span');
    text.className = 'topnav__company-name';
    text.textContent = name;
    label.appendChild(text);
    return label;
  }
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'topnav__company topnav__company--switch';
  btn.setAttribute('aria-label', t('nav.company.switch', { name }));
  const text = document.createElement('span');
  text.className = 'topnav__company-name';
  text.textContent = name;
  const caret = icon('chevron-down');
  caret.classList.add('topnav__company-caret');
  btn.append(text, caret);
  btn.addEventListener('click', () => onSwitch());
  return btn;
}

// TopNav — the desktop top navigation: brand mark + name and the active-company
// element on the left, the module links, the health pill, and the account menu
// on the right. Hidden below 768px (nav.css).
export class TopNav implements Component {
  private root: HTMLElement | null = null;
  private links: HTMLElement | null = null;
  private company: HTMLElement | null = null;
  private pill: HealthPill | null = null;
  private accountMenu: AccountMenu | null = null;

  mount(host: HTMLElement): void {
    const nav = document.createElement('header');
    nav.className = 'topnav';

    const inner = document.createElement('div');
    inner.className = 'topnav__inner';

    // Left cluster: the brand identity (mark + name) and the active company.
    const identity = document.createElement('div');
    identity.className = 'topnav__identity';

    const brand = document.createElement('a');
    brand.className = 'topnav__brand';
    brand.href = '/';
    // The square brand mark (FE-022.1) sits to the LEFT of the name. It is
    // opaque (a gold mark on a solid dark-purple tile), so — unlike the login
    // wordmark — it needs no theme-swap variant; on the dark header nav.css
    // draws its edge in gold (issue 870). Decorative:
    // the adjacent name span is the brand's accessible name, so the img is hidden
    // from the accessibility tree to avoid a redundant "Fexo Enterprise" reading.
    const mark = document.createElement('img');
    mark.className = 'topnav__brand-mark';
    mark.src = '/brand/icon.png';
    mark.alt = '';
    mark.setAttribute('aria-hidden', 'true');
    mark.width = 120;
    mark.height = 120;
    // The name rides its own span so the tablet band (768–1023px, nav.css) can
    // hide it VISUALLY while it stays the link's accessible name — the header
    // collapses to the mark there to fit one line (issue 720).
    const name = document.createElement('span');
    name.className = 'topnav__brand-name';
    name.textContent = t('common.brand');
    brand.append(mark, name);

    // The active-company slot is rebuilt on every RBAC_CONTEXT_EVENT: the brand
    // <a> is built once here, but the company changes with the session's bound
    // company, so renderCompany() re-runs from refresh() (FE-022.2). Living beside
    // the brand — outside the rebuilt .topnav__links region — it does not ride the
    // links refresh for free, hence the explicit slot.
    const company = document.createElement('div');
    company.className = 'topnav__company-slot';
    this.company = company;

    identity.append(brand, company);

    const links = document.createElement('nav');
    links.className = 'topnav__links';
    this.links = links;
    this.renderLinks();
    this.renderCompany();

    inner.append(identity, links);
    nav.appendChild(inner);
    host.appendChild(nav);
    this.root = nav;
  }

  // refresh re-filters the nav against the current RBAC mirror — both the
  // capability-filtered links and the active-company element (grants and the
  // bound company both change on a company switch, RBAC-006.7 / FE-022.2).
  refresh(): void {
    this.renderLinks();
    this.renderCompany();
  }

  // renderCompany rebuilds the active-company element from the RBAC mirror. It
  // shows the active company's name (FE-022.2); when the caller holds more than
  // one company the element switches company via the shared picker (FE-022.3),
  // otherwise it is a plain label. Nothing renders while unbound or before the
  // mirror loads — there is no company to name yet.
  private renderCompany(): void {
    const slot = this.company;
    if (!slot) return;
    slot.replaceChildren();
    const ctx = rbacContext();
    const companies = ctx?.companies ?? [];
    const activeId = ctx?.activeCompanyId ?? null;
    const active = companies.find((c) => c.companyId === activeId) ?? null;
    if (!active) return;
    slot.appendChild(
      buildCompanyElement(active.name, companies.length > 1, () =>
        void this.promptSwitch(companies, activeId),
      ),
    );
  }

  // promptSwitch opens the shared company picker and binds the chosen company —
  // the same path the account menu's "Switch company" row uses (FE-022.3).
  // switchCompany broadcasts RBAC_CONTEXT_EVENT, so refresh() rebuilds this
  // element (and the links) under the new company; nothing to update here.
  private async promptSwitch(
    companies: CompanyMembership[],
    activeId: string | null,
  ): Promise<void> {
    const chosen = await promptCompanyPicker(companies, {
      title: t('me.switchCompany'),
      message: t('me.switchCompanyMsg'),
      cancelable: true,
      activeCompanyId: activeId ?? undefined,
    });
    if (!chosen || chosen === activeId) return;
    try {
      await switchCompany(chosen);
    } catch {
      // Keep the current company if the switch is rejected.
    }
  }

  // renderLinks rebuilds the capability-filtered links, re-mounting the health
  // pill and account menu after them (both live at the end of the same row).
  private renderLinks(): void {
    const links = this.links;
    if (!links) return;
    this.pill?.unmount();
    this.pill = null;
    this.accountMenu?.unmount();
    this.accountMenu = null;
    links.replaceChildren();

    for (const { href, labelKey } of topNavItems()) {
      const a = document.createElement('a');
      a.className = 'topnav__link';
      a.href = href;
      a.textContent = t(labelKey);
      // Top-nav links light only on their own page (exact match); the tab bar
      // uses prefix matching so a parent tab stays lit on sub-pages.
      if (location.pathname === href) a.setAttribute('aria-current', 'page');
      links.appendChild(a);
    }

    // Admin is one top-nav entry sat at the end of the module links (FE-017,
    // amending FE-011's D13 dropdown): a DIRECT link into the admin section — no
    // preview dropdown — landing on the caller's first reachable destination
    // (adminMenuItems()[0], /admin/roles for a full admin, varies per caller by
    // RBAC). Once inside, the horizontal section bar (AdminRail) switches lenses.
    // It renders only when the caller can reach at least one admin destination —
    // so a capability-less or phone-bound session gets no Admin entry (FE-017.2,
    // regression-guarding FE-011.2). Unlike the flat module links above (exact
    // match), it lights across the whole /admin/* prefix via isAdminArea.
    const admin = adminMenuItems();
    if (admin.length > 0) {
      const a = document.createElement('a');
      a.className = 'topnav__link';
      a.href = admin[0].href;
      a.textContent = t('nav.admin');
      if (isAdminArea()) a.setAttribute('aria-current', 'page');
      links.appendChild(a);
    }

    this.pill = new HealthPill();
    this.pill.mount(links);

    // The account menu sits at the right end of the row. Rebuilt in place with
    // the pill so a company switch (RBAC_CONTEXT_EVENT) refreshes the identity it
    // shows and re-filters its route entries under the new role (FE-010).
    this.accountMenu = new AccountMenu();
    this.accountMenu.mount(links);
  }

  unmount(): void {
    this.pill?.unmount();
    this.pill = null;
    this.accountMenu?.unmount();
    this.accountMenu = null;
    this.links = null;
    this.company = null;
    this.root?.remove();
    this.root = null;
  }
}

// TabBar — the phone bottom navigation: the four thumb-reachable field
// destinations as icon + label tabs. Hidden at 768px and up (nav.css).
export class TabBar implements Component {
  private root: HTMLElement | null = null;

  mount(host: HTMLElement): void {
    const bar = document.createElement('nav');
    bar.className = 'tabbar';
    bar.setAttribute('aria-label', t('nav.tabbar'));

    this.root = bar;
    this.renderItems();

    host.appendChild(bar);
  }

  // refresh re-filters the tab bar against the current RBAC mirror.
  refresh(): void {
    this.renderItems();
  }

  private renderItems(): void {
    const bar = this.root;
    if (!bar) return;
    bar.replaceChildren();

    for (const item of tabBarItems()) {
      const a = document.createElement('a');
      a.className = 'tabbar__item';
      a.href = item.href;
      if (isActiveTab(item.href)) a.setAttribute('aria-current', 'page');

      const iconBox = document.createElement('span');
      iconBox.className = 'tabbar__icon';
      if (item.icon) iconBox.appendChild(icon(item.icon));

      const label = document.createElement('span');
      label.textContent = t(item.tabLabelKey ?? item.labelKey);

      a.append(iconBox, label);
      bar.appendChild(a);
    }
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }
}
